<script setup lang="ts">
import type { CommitmentResponse, EventResponse } from '@@/types/api';
import {
  fundingDisplay,
  isOpenFundraising,
  isTicketSalesClosed,
} from '~/utils/event-policy';

const props = defineProps<{
  commitment: CommitmentResponse;
  event: EventResponse;
}>();

const isGoalMet = computed(() => fundingDisplay(props.event).isGoalMet);

const isMultiType = computed(
  () => (props.event.registration_types?.length ?? 0) > 1,
);

const canSwitchType = computed(
  () =>
    isMultiType.value &&
    props.commitment.status === 'committed' &&
    isOpenFundraising(props.event),
);

const canUpdatePartySize = computed(
  () =>
    props.commitment.status === 'committed' && isOpenFundraising(props.event),
);

const canPurchaseAdditional = computed(
  () =>
    props.event.user_facing_status === 'Confirmed' &&
    !(props.event.is_sold_out ?? false) &&
    !isTicketSalesClosed(props.event),
);

const isDeclined = computed(
  () => props.commitment.payment_status === 'awaiting_retry',
);

const retryTimeDisplay = computed(() => {
  if (!props.commitment.next_retry_at) return null;
  const diff = new Date(props.commitment.next_retry_at).getTime() - Date.now();
  if (diff <= 0) return 'soon';
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
});

const emit = defineEmits<{
  'update-payment-method': [];
  withdraw: [];
  'switch-type': [];
  'update-party-size': [];
  'purchase-additional': [];
}>();
</script>

<template>
  <div class="backer-status-card">
    <div class="d-flex align-center mb-3">
      <v-icon v-if="isDeclined" color="warning" class="mr-2"
        >mdi-alert-circle</v-icon
      >
      <v-icon v-else color="success" class="mr-2">mdi-check-circle</v-icon>
      <span class="text-subtitle-1 font-weight-bold">
        {{ isDeclined ? 'Payment declined' : "You're committed" }}
      </span>
    </div>

    <!-- Registration type badge (multi-type only) -->
    <v-chip
      v-if="commitment.registration_type && isMultiType"
      size="small"
      label
      class="mb-3"
    >
      {{ commitment.registration_type.name }}
    </v-chip>

    <v-alert v-if="isDeclined" type="warning" variant="tonal" class="mb-4">
      <div class="text-body-2">
        {{ commitment.last_payment_error || 'Your payment was declined.' }}
      </div>
      <div v-if="retryTimeDisplay" class="text-body-2 mt-1">
        Next automatic retry {{ retryTimeDisplay }}.
      </div>
      <div class="text-body-2 mt-1 font-weight-medium">
        Update your payment method to keep your spot.
      </div>
    </v-alert>

    <p v-if="!isDeclined" class="text-body-2 text-medium-emphasis mb-4">
      <template v-if="isGoalMet">
        The funding goal has been reached. Your card will be charged when
        fundraising closes.
      </template>
      <template v-else>
        Your card will be charged if this event reaches its funding goal.
      </template>
    </p>

    <!-- Payment method summary -->
    <div
      v-if="commitment.card_last_four"
      class="d-flex align-center mb-3 pa-3 rounded-lg bg-surface-variant"
    >
      <v-icon size="small" class="mr-2">mdi-credit-card-outline</v-icon>
      <span class="text-body-2">
        Card &bull;&bull;&bull;&bull; {{ commitment.card_last_four }}
      </span>
      <v-spacer />
      <span
        v-if="
          commitment.card_exp_month != null && commitment.card_exp_year != null
        "
        class="text-body-2 text-medium-emphasis"
      >
        {{ String(commitment.card_exp_month).padStart(2, '0') }}/{{
          commitment.card_exp_year
        }}
      </span>
    </div>

    <v-btn
      :variant="isDeclined ? 'elevated' : 'text'"
      :size="isDeclined ? 'default' : 'small'"
      color="primary"
      class="mb-2"
      @click="emit('update-payment-method')"
    >
      Update payment method
    </v-btn>

    <v-btn
      v-if="canUpdatePartySize && !isDeclined"
      variant="text"
      size="small"
      color="primary"
      class="mb-2"
      @click="emit('update-party-size')"
    >
      Update party size
    </v-btn>

    <v-btn
      v-if="canSwitchType && !isDeclined"
      variant="text"
      size="small"
      color="primary"
      class="mb-2"
      @click="emit('switch-type')"
    >
      Switch registration type
    </v-btn>

    <v-btn
      v-if="canPurchaseAdditional && !isDeclined"
      variant="outlined"
      size="small"
      color="primary"
      block
      class="mb-2"
      @click="emit('purchase-additional')"
    >
      Buy additional tickets
    </v-btn>

    <v-btn
      v-if="commitment.can_withdraw && !isDeclined"
      variant="outlined"
      size="small"
      block
      @click="emit('withdraw')"
    >
      Withdraw commitment
    </v-btn>
  </div>
</template>
